import React, { useCallback } from 'react';
import debouncePromise from 'debounce-promise';
import { useIntl } from 'react-intl-next';
import Avatar from '@atlaskit/avatar';
import { ColumnFilterGroup, FilterColLabelText, FilterRowLabelText, FilterShowMore, OptionVariant, RowFilterGroup } from '@atlassian/search-dialog';
import { AsyncSelectFilterComponent } from '@atlassian/search-dialog';
import { FilterOptionSource } from '../../../../../../common/filters/types';
import { useAnalytics, onFilterSelect } from '../../../../../../common/analytics';
import { ProductFilterItem } from '../../../../../../common/product-search-filter';
import { messages } from '../../../../../../messages';
import { FilterOptionItem } from './filter-option-item';
var SHOW_MORE_DEBOUNCE_TIME = 250;
export var CheckboxListFilter = function CheckboxListFilter(_ref) {
  var _filterConfig$filterO;
  var filterType = _ref.filterType,
    store = _ref.store,
    loadFilterOptions = _ref.loadFilterOptions,
    sectionLabel = _ref.sectionLabel,
    findMoreLabel = _ref.findMoreLabel,
    maxSelectable = _ref.maxSelectable,
    filterConfig = _ref.filterConfig;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAnalytics = useAnalytics(),
    fireAnalyticsEvent = _useAnalytics.fireAnalyticsEvent;
  var availableFilters = store.availableFilters,
    updateFilter = store.updateFilter,
    addFilters = store.addFilters,
    isLoading = store.isLoading;
  var usePreloadedOptions = (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.usePreloadedOptions) || false;
  var showIcons = !(filterConfig !== null && filterConfig !== void 0 && filterConfig.hideIcon);
  var appearanceIcon = (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterOptionIconShape) || 'square';
  var options = availableFilters.map(function (filterOption) {
    return {
      label: /*#__PURE__*/React.createElement(FilterOptionItem, {
        label: filterOption.label,
        iconProps: {
          src: filterOption.avatarUrl,
          appearance: filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterOptionIconShape
        }
      }),
      value: filterOption
    };
  });
  var visibleOptions = options.filter(function (option) {
    return option.value.isVisible;
  });
  var onChangeCallback = useCallback(function (value, _, checked) {
    updateFilter(value, checked);
  }, [updateFilter]);
  var addFilter = useCallback(function (_ref2) {
    var value = _ref2.value;
    addFilters([value]);
    updateFilter(value.id, true);
    fireAnalyticsEvent(onFilterSelect({
      filterId: value.id,
      filterSource: FilterOptionSource.EXTERNAL,
      filterType: filterType
    }));
  }, [addFilters, updateFilter, fireAnalyticsEvent, filterType]);
  var loadMoreFilters = debouncePromise(function (query) {
    return loadFilterOptions(query);
  }, SHOW_MORE_DEBOUNCE_TIME);
  var loadAvatar = useCallback(function (_ref3) {
    var iconUrl = _ref3.avatarUrl,
      name = _ref3.name;
    if (showIcons) {
      return /*#__PURE__*/React.createElement(Avatar, {
        borderColor: "transparent",
        src: iconUrl,
        appearance: appearanceIcon,
        size: 'small',
        name: name
      });
    }
  }, [showIcons, appearanceIcon]);
  var checkedValues = availableFilters.filter(function (filter) {
    return filter.isChecked;
  });
  var showMoreOptions = options.filter(function (option) {
    return !option.value.isVisible;
  });
  var filterPreloadedOptions = function filterPreloadedOptions(query) {
    return Promise.resolve(showMoreOptions.filter(function (option) {
      return option.value.label.indexOf(query.trim().toLowerCase()) !== -1;
    }));
  };
  var isMaxValuesSelected = !!maxSelectable && checkedValues.length === maxSelectable;
  var loadFilterItems = function loadFilterItems() {
    var isColumnGrouped = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    return visibleOptions.map(function (_ref4) {
      var _filterConfig$labelCo;
      var value = _ref4.value;
      return /*#__PURE__*/React.createElement(ProductFilterItem, {
        value: value.id,
        filterType: filterType,
        icon: loadAvatar(value),
        label: value.label,
        onChange: onChangeCallback,
        isChecked: value.isChecked,
        filterSource: value.filterSource,
        isDisabled: isMaxValuesSelected && !value.isChecked,
        variant: OptionVariant.CHECKBOX,
        LabelComponent: (_filterConfig$labelCo = filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.labelComponent) !== null && _filterConfig$labelCo !== void 0 ? _filterConfig$labelCo : isColumnGrouped ? FilterColLabelText : FilterRowLabelText
      });
    });
  };
  var onFocusAriaMessage = useCallback(function (props) {
    var focused = props.focused,
      options = props.options;
    var optionIndex = options === null || options === void 0 ? void 0 : options.findIndex(function (option) {
      return option === focused;
    });
    return "Option ".concat(focused.value.label, " focused, ").concat(optionIndex + 1, " out of ").concat(options.length, ".");
  }, []);
  var loadFilterShowMore = function loadFilterShowMore() {
    return !(usePreloadedOptions && showMoreOptions.length === 0) && /*#__PURE__*/React.createElement(FilterShowMore, {
      placeholderText: findMoreLabel,
      addFilter: addFilter,
      defaultOptions: showMoreOptions || [],
      loadOptions: usePreloadedOptions ? filterPreloadedOptions : loadMoreFilters,
      buttonLabel: findMoreLabel,
      buttonText: formatMessage(messages.show_more_filters),
      filterComponent: AsyncSelectFilterComponent,
      loadingMessage: function loadingMessage() {
        return formatMessage(messages.loading_filters);
      },
      noOptionsMessage: function noOptionsMessage() {
        return formatMessage(messages.no_options_filters);
      },
      isDisabled: isMaxValuesSelected,
      onFocusAriaMessage: onFocusAriaMessage
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, (!(filterConfig !== null && filterConfig !== void 0 && filterConfig.filterGroupedBy) || (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterGroupedBy) === 'column') && /*#__PURE__*/React.createElement(ColumnFilterGroup, {
    title: sectionLabel,
    isLoading: isLoading,
    avatarShape: (_filterConfig$filterO = filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterOptionIconShape) !== null && _filterConfig$filterO !== void 0 ? _filterConfig$filterO : 'circle'
  }, loadFilterItems(true), loadFilterShowMore()), (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterGroupedBy) === 'row' && /*#__PURE__*/React.createElement(RowFilterGroup, {
    title: sectionLabel,
    isLoading: false
  }, loadFilterItems(false), loadFilterShowMore()));
};